import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Pagination, Grid, Typography, Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Error from '../Error';
import Spinner from '../Spinner';
import { useGetCodesLotQuery } from '../../features/api/rest/apiLotsSlice';
import QrCodeCard from './QrCodeCard';
import QrCodeFilters from './QrCodeFilters';
import { getSuspect, getUsed, getClean } from '../../features/qrCodeFilters';
import AddCodeDialog from './AddCodeDialog';

function QrCodesLot({ ean, opk, lpk, searchParamsName = '' }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const modifiedSearchParamsName = searchParamsName ? `${searchParamsName}_` : '';
  const codesForPage = parseInt(searchParams.get(`${modifiedSearchParamsName}psize`), 10) || 10;
  const codePage = parseInt(searchParams.get(`${modifiedSearchParamsName}page`), 10) || 1;
  const isSuspect = useSelector(getSuspect);
  const isUsed = useSelector(getUsed);
  const isClean = useSelector(getClean);
  const [usedCodes, setUsedCodes] = useState([]);
  const [isAdding, setIsAdding] = useState(false);

  const { codes, codesCount, isLoading, isFetching, isError } = useGetCodesLotQuery(
    { ean, opk, lpk, page: codePage, psize: 10, ordering: '-id' },
    {
      selectFromResult: (result) => ({
        codes: result.data?.results,
        codesCount: result.data?.count || 0,
      }),
    }
  );

  useEffect(() => {
    if (codes) {
      if (isSuspect || isUsed || isClean) handleFilter();
      else setUsedCodes(codes);
    }
  }, [codes, isSuspect, isUsed, isClean]);

  const handleFilter = () => {
    let codesToFilterBySuspect = [];
    let codesToFilterByUse = [];
    let codesToFilterByClean = [];
    let codesToFilter = codes;
    let filteredCodes = [];
    if (isSuspect) codesToFilterBySuspect = codes.filter((code) => code.suspect !== true);
    if (isUsed) codesToFilterByUse = codes.filter((code) => code.used !== true);
    if (isClean) codesToFilterByClean = codes.filter((code) => code.used !== false || code.suspect !== false);
    filteredCodes = [...codesToFilterBySuspect, ...codesToFilterByUse, ...codesToFilterByClean];
    if (filteredCodes?.length > 0) {
      codesToFilter = codes.filter((code) => !filteredCodes.includes(code));
    }
    setUsedCodes(codesToFilter);
  };

  const handlePageChange = (page) => {
    searchParams.set(`${modifiedSearchParamsName}page`, page);
    navigate(`${pathname}?${searchParams.toString()}`);
  };

  const pagination = <Pagination sx={{ my: 2 }} count={Math.ceil(codesCount / codesForPage)} page={codePage} onChange={(event, newPage) => handlePageChange(newPage)} />;

  if (isLoading || isFetching) return <Spinner />;

  if (isError) return <Error />;

  return (
    <>
      <Box>
        <Button
          sx={{ mt: 2 }}
          startIcon={<AddIcon />}
          variant="contained"
          color="primary"
          onClick={() => {
            setIsAdding(true);
          }}
        >
          {t('organizations.product.lots.addCodes.dialog.title')}
        </Button>
        {codes?.length > 0 && (
          <Grid container sx={{ gap: 2, display: 'flex', justifyContent: 'left' }}>
            <Grid item>
              <Typography variant="h6" sx={{ mt: 3.5 }}>
                {t('organizations.product.lots.qrCode.filterBy')}:
              </Typography>
            </Grid>
            <QrCodeFilters />
          </Grid>
        )}
        {usedCodes && Object.keys(usedCodes).length > 0 ? (
          <>
            {pagination}
            <Grid id="Product_Grid">{usedCodes && usedCodes.map((code) => <QrCodeCard key={code.code} ean={ean} opk={opk} lpk={lpk} code={code.code} suspect={code.suspect} used={code.used} />)}</Grid>
            {pagination}
          </>
        ) : (
          <Typography variant="h5" sx={{ mt: 3 }}>
            {t('organizations.product.lots.qrCode.noCodes')}
          </Typography>
        )}
      </Box>
      <AddCodeDialog ean={ean} opk={opk} lpk={lpk} isOpen={isAdding} setIsOpen={setIsAdding} />
    </>
  );
}

export default QrCodesLot;

QrCodesLot.propTypes = {
  ean: PropTypes.string.isRequired,
  opk: PropTypes.string.isRequired,
  lpk: PropTypes.string.isRequired,
  searchParamsName: PropTypes.string,
};
